/**
 * TanStack Mutation Hook Factory - createMutationHook
 *
 * Generic factory for creating standardized mutation hooks with:
 * - Automatic API client injection via useApiClient()
 * - Built-in logging support
 * - Automatic query invalidation on success
 * - Type-safe variables and return types
 * - Consistent error handling
 *
 * @example
 * ```typescript
 * // Create a hook for creating a document
 * export const useCreateDocument = createMutationHook({
 *   mutationFn: (apiClient, data: DocumentCreate) =>
 *     apiClient.createDocumentApiDocumentsPost(data).then(r => r.data),
 *   invalidateQueries: [['documents']],
 *   logContext: 'POST /api/documents',
 * });
 *
 * // Usage in component:
 * const createDoc = useCreateDocument();
 * await createDoc.mutateAsync({ title: 'New Doc', content: '...' });
 * ```
 */

import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useApiClient } from '../useApiClient';
import type { ApiClient } from '../../../api/ApiClient';
import { log } from '../../../utils/errors/logger';

/**
 * Configuration for creating a mutation hook
 */
export interface CreateMutationHookConfig<TData, TVariables> {
  /**
   * Mutation function that receives API client and variables
   * Should return a Promise with the mutation result
   *
   * @example
   * ```typescript
   * mutationFn: (apiClient, data: DocumentCreate) =>
   *   apiClient.createDocumentApiDocumentsPost(data).then(r => r.data)
   * ```
   */
  mutationFn: (apiClient: ApiClient<unknown>, variables: TVariables) => Promise<TData>;

  /**
   * Query keys to invalidate on successful mutation
   * Causes related queries to refetch their data
   *
   * @example
   * ```typescript
   * invalidateQueries: [
   *   ['documents'],           // Invalidate all document queries
   *   ['documents', 'list'],   // Invalidate document list queries
   * ]
   * ```
   */
  invalidateQueries?: readonly (readonly unknown[])[];

  /**
   * Query keys to remove from cache on successful mutation
   * Useful for delete operations
   *
   * @example
   * ```typescript
   * removeQueries: [
   *   ['documents', documentId],  // Remove specific document from cache
   * ]
   * ```
   */
  removeQueries?: readonly (readonly unknown[])[];

  /**
   * Optional logging context for request/response/error logging
   * If provided, will automatically log all mutation interactions
   *
   * @example 'POST /api/documents', 'DELETE /api/documents/:id'
   */
  logContext?: string;

  /**
   * HTTP method for logging purposes
   * Defaults to 'MUTATION'
   */
  logMethod?: string;

  /**
   * Optimistic update handler
   * Called before mutation starts, useful for optimistic UI updates
   * Should return rollback context that will be passed to onError if mutation fails
   */
  onMutate?: (variables: TVariables, queryClient: ReturnType<typeof useQueryClient>) => Promise<unknown> | unknown;

  /**
   * Custom success handler
   * Called after successful mutation and query invalidation
   */
  onSuccess?: (data: TData, variables: TVariables) => void;

  /**
   * Custom error handler
   * Called in addition to standard error logging
   */
  onError?: (error: Error, variables: TVariables, context: unknown) => void;
}

/**
 * Options that can be passed when using the created mutation hook
 */
export interface MutationHookOptions<TData = unknown, TVariables = unknown> {
  /**
   * Additional TanStack Mutation options to override defaults
   */
  mutationOptions?: Partial<UseMutationOptions<TData, Error, TVariables>>;
}

/**
 * Factory function to create standardized TanStack Mutation hooks
 *
 * Creates a React hook that:
 * 1. Automatically uses the API client via useApiClient()
 * 2. Logs requests, responses, and errors (if logContext provided)
 * 3. Invalidates specified queries on success
 * 4. Provides type-safe variables and return types
 * 5. Supports custom options on usage
 *
 * @template TData - The type of data returned by the mutation
 * @template TVariables - The type of variables the mutation accepts
 *
 * @param config - Configuration object for the mutation hook
 * @returns A React hook that can be used in components
 *
 * @example
 * ```typescript
 * // Create mutation hook
 * export const useCreateDocument = createMutationHook({
 *   mutationFn: (apiClient, data: DocumentCreate) =>
 *     apiClient.createDocumentApiDocumentsPost(data).then(r => r.data),
 *   invalidateQueries: [['documents']],
 *   logContext: 'POST /api/documents',
 *   logMethod: 'POST',
 * });
 *
 * // Usage in component:
 * const createDoc = useCreateDocument();
 *
 * const handleCreate = async () => {
 *   try {
 *     const newDoc = await createDoc.mutateAsync({
 *       title: 'My Document',
 *       content: 'Document content...',
 *     });
 *     console.log('Created:', newDoc);
 *   } catch (error) {
 *     console.error('Failed to create:', error);
 *   }
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Delete mutation with query removal
 * export const useDeleteDocument = createMutationHook({
 *   mutationFn: (apiClient, documentId: string) =>
 *     apiClient.deleteDocumentApiDocumentsDocumentIdDelete(documentId).then(r => r.data),
 *   invalidateQueries: [['documents']],
 *   removeQueries: [(documentId: string) => ['documents', documentId]],
 *   logContext: 'DELETE /api/documents/:id',
 *   logMethod: 'DELETE',
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Mutation with custom handlers
 * export const useUpdateDocument = createMutationHook({
 *   mutationFn: (apiClient, { id, data }: { id: string; data: DocumentUpdate }) =>
 *     apiClient.updateDocumentApiDocumentsDocumentIdPut(id, data).then(r => r.data),
 *   invalidateQueries: [['documents']],
 *   logContext: 'PUT /api/documents/:id',
 *   logMethod: 'PUT',
 *   onSuccess: (data, variables) => {
 *     console.log(`Document ${variables.id} updated successfully`);
 *   },
 *   onError: (error, variables) => {
 *     console.error(`Failed to update document ${variables.id}:`, error);
 *   },
 * });
 * ```
 */
export function createMutationHook<TData, TVariables>(
  config: CreateMutationHookConfig<TData, TVariables>
): (
  options?: MutationHookOptions<TData, TVariables>
) => UseMutationResult<TData, Error, TVariables> {
  return (options?: MutationHookOptions<TData, TVariables>) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation<TData, Error, TVariables>({
      mutationFn: async (variables: TVariables) => {
        const method = config.logMethod || 'MUTATION';

        // Log request if context provided
        if (config.logContext) {
          log.api.request(method, config.logContext, variables);
        }

        try {
          const result = await config.mutationFn(apiClient, variables);

          // Log response if context provided
          if (config.logContext) {
            log.api.response(method, config.logContext, 200, result);
          }

          return result;
        } catch (error) {
          // Log error
          if (config.logContext) {
            log.api.error(method, config.logContext, error as Error);
          }

          throw error;
        }
      },
      onMutate: async (variables: TVariables) => {
        // Call custom onMutate handler
        if (config.onMutate) {
          return await config.onMutate(variables, queryClient);
        }

        // Call user-provided onMutate (standard TanStack Query signature)
        if (options?.mutationOptions?.onMutate) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return await (options.mutationOptions.onMutate as any)(variables);
        }
      },
      onSuccess: (data, variables) => {
        // Invalidate queries
        if (config.invalidateQueries) {
          config.invalidateQueries.forEach((queryKey) => {
            void queryClient.invalidateQueries({ queryKey: [...queryKey] });
          });
        }

        // Remove queries from cache
        if (config.removeQueries) {
          config.removeQueries.forEach((queryKey) => {
            void queryClient.removeQueries({ queryKey: [...queryKey] });
          });
        }

        // Call custom success handler
        if (config.onSuccess) {
          config.onSuccess(data, variables);
        }

        // Call user-provided onSuccess
        if (options?.mutationOptions?.onSuccess) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (options.mutationOptions.onSuccess as any)(data, variables);
        }
      },
      onError: (error, variables, context) => {
        // Call custom error handler
        if (config.onError) {
          config.onError(error, variables, context);
        }

        // Call user-provided onError
        if (options?.mutationOptions?.onError) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (options.mutationOptions.onError as any)(error, variables, context);
        }
      },
      // Spread other mutation options, but exclude lifecycle hooks to avoid conflicts
      ...(() => {
        if (!options?.mutationOptions) {return {};}
        const { onMutate, onSuccess, onError, ...rest } = options.mutationOptions;
        return rest;
      })(),
    });
  };
}

/**
 * Helper type for extracting mutation variables from a mutation hook
 * Useful for type inference in complex scenarios
 *
 * @example
 * ```typescript
 * const createDoc = useCreateDocument();
 * type CreateDocVars = MutationVariables<typeof createDoc>;
 * ```
 */
export type MutationVariables<T> = T extends UseMutationResult<any, any, infer V, any>
  ? V
  : never;

/**
 * Helper type for extracting mutation data from a mutation hook
 *
 * @example
 * ```typescript
 * const createDoc = useCreateDocument();
 * type CreateDocData = MutationData<typeof createDoc>;
 * ```
 */
export type MutationData<T> = T extends UseMutationResult<infer D, any, any, any>
  ? D
  : never;
